'use client';

import { type FormEvent, useEffect, useRef, useState } from 'react';

interface TotpChallengeFormProps {
  loading: boolean;
  onSubmit: (code: string) => void;
  onCancel: () => void;
}

/**
 * パスワードの次に、認証アプリの 6 桁コードを入力する画面（ADR-0043）。
 * 見た目は SignInForm のカードに揃える。
 */
export function TotpChallengeForm({ loading, onSubmit, onCancel }: TotpChallengeFormProps) {
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isComplete = /^\d{6}$/.test(code);

  // パスワード送信の直後に表示されるので、すぐ入力できるようにする
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isComplete && !loading) {
      onSubmit(code);
    }
  };

  return (
    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="totp-challenge-form">
        <div className="space-y-2">
          <h1 className="text-center text-2xl font-bold text-gray-900">確認コードの入力</h1>
          <p className="text-center text-sm text-gray-600">
            認証アプリに表示されている 6 桁のコードを入力してください。
          </p>
        </div>

        <div>
          <label htmlFor="totp-code" className="block text-sm font-medium text-gray-700">
            確認コード
          </label>
          <div className="mt-1">
            <input
              id="totp-code"
              name="totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              ref={inputRef}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={loading}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm tracking-widest"
              placeholder="123456"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !isComplete}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
        >
          {loading ? '確認中...' : '確認してサインイン'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="w-full text-center text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50 transition-colors duration-150"
        >
          メールアドレスとパスワードの入力に戻る
        </button>

        <p className="text-center text-xs text-gray-500">
          認証アプリを失くした場合は、サポートへご連絡のうえ{' '}
          <a href="/auth/mfa-recovery" className="text-indigo-600 hover:text-indigo-500">
            復旧の手続き
          </a>
          を行ってください。
        </p>
      </form>
    </div>
  );
}
